/**
 * PATH: packages/job-hub/src/jobs/tabs/job-qa/phone/qaPhoneRowActions.ts
 * FILE_ID: JOBHUB-QA-PHONE-ROWACTIONS-001
 * PURPOSE: Same insert/duplicate/hold/move maths as QaChecklistTable, for the More sheet.
 */
import {
  distinctProducts,
  insertIdAt,
  moveSibling,
  toSortOrderPayload,
} from '../../../qa/checklistTable'
import type { QueryClient } from '@tanstack/react-query'
import type { QaApi, QaAreaExecution, QaItemEdit, QaItemInstance } from '../../../qa/qaApi'
import { qaNeedsSignoff } from '../../../qa/qaHoldPoints'
import { applyQaDelete } from '../qaSelectionWrites'

export function resolvePhoneRow(
  items: QaItemInstance[],
  expandedRowId: string | null,
  selectedIds: string[],
) {
  return items.find((item) => item.id === expandedRowId)
    ?? items.find((item) => selectedIds.includes(item.id))
    ?? items[0]
}

export function phoneRowSiblingFlags(items: QaItemInstance[], item: QaItemInstance) {
  const parent = item.parent_item ?? null
  const index = items.findIndex((row) => row.id === item.id)
  return {
    isFirst: !items.slice(0, Math.max(0, index)).some((row) => (row.parent_item ?? null) === parent),
    isLast: !items.slice(index + 1).some((row) => (row.parent_item ?? null) === parent),
  }
}

export async function insertPhoneRow(
  api: Pick<QaApi, 'addItem' | 'reorderItems'>,
  areaId: string,
  items: QaItemInstance[],
  targetIndex: number,
  fields: QaItemEdit,
  parentItemId?: string | null,
  insertAfterNumber?: string | null,
  checklistId?: string | null,
  origin?: 'standard' | 'quote' | 'manual',
) {
  const created = await api.addItem(areaId, fields, parentItemId, insertAfterNumber, checklistId, origin)
  await api.reorderItems(toSortOrderPayload(
    insertIdAt(items.map((item) => item.id), created.id, targetIndex),
  ))
}

export async function movePhoneRow(
  api: Pick<QaApi, 'reorderItems'>,
  items: QaItemInstance[],
  item: QaItemInstance,
  dir: -1 | 1,
) {
  const index = items.findIndex((row) => row.id === item.id)
  const next = moveSibling(items, index, dir)
  if (next.map((row) => row.id).join() === items.map((row) => row.id).join()) return
  await api.reorderItems(toSortOrderPayload(next.map((row) => row.id)))
}

export function phoneAiProducts(items: QaItemInstance[], fallback: string) {
  const products = distinctProducts(items)
  return products.length ? products : [fallback]
}

export function buildPhoneRowMenuActions(
  api: Pick<QaApi, 'addItem' | 'reorderItems' | 'setHoldPoint' | 'setSignoffRequired' | 'deleteItem' | 'deleteChecklist' | 'deleteArea'>,
  areaId: string | undefined,
  items: QaItemInstance[],
  target: QaItemInstance | undefined,
  checklistId: string | null,
  afterMutate: () => void,
  queryClient: QueryClient,
  execution: QaAreaExecution | null,
  inspectionId: string | null,
) {
  const origin = target?.origin === 'standard' ? 'standard' : 'quote'
  const index = target ? items.findIndex((row) => row.id === target.id) : -1
  return {
    onInsertAbove: () => {
      if (!areaId || !target) return
      void insertPhoneRow(api, areaId, items, index, {}, target.parent_item ?? null, null, target.checklist_instance, origin)
        .then(afterMutate)
    },
    onInsertBelow: () => {
      if (!areaId || !target) return
      void insertPhoneRow(api, areaId, items, index + 1, {}, target.parent_item ?? null, target.item_number, target.checklist_instance, origin)
        .then(afterMutate)
    },
    onInsertSubRow: () => {
      if (!areaId || !target) return
      void insertPhoneRow(api, areaId, items, index + 1, {}, target.template_item ?? null)
        .then(afterMutate)
    },
    onDuplicate: () => {
      if (!areaId || !target) return
      void insertPhoneRow(api, areaId, items, index + 1, {
        title: target.title,
        product: target.product ?? '',
        requirement_text: target.requirement_text ?? '',
        acceptance_criteria: target.acceptance_criteria ?? '',
        has_hold_point: Boolean(target.has_hold_point),
      }, target.parent_item ?? null, target.item_number, target.checklist_instance, origin)
        .then(afterMutate)
    },
    onToggleHold: () => {
      if (!target) return
      void api.setHoldPoint(target.id, !target.has_hold_point).then(afterMutate)
    },
    onToggleSignoff: () => {
      if (!target) return
      void api.setSignoffRequired(target.id, !qaNeedsSignoff(target)).then(afterMutate)
    },
    onMoveUp: () => {
      if (!target) return
      void movePhoneRow(api, items, target, -1).then(afterMutate)
    },
    onMoveDown: () => {
      if (!target) return
      void movePhoneRow(api, items, target, 1).then(afterMutate)
    },
    onDelete: () => {
      if (!target) return
      void applyQaDelete({
        api,
        queryClient,
        inspectionId,
        executions: execution ? [execution] : [],
        targets: { items: [target.id], checklists: [], areas: [] },
      })
    },
  }
}
